import { useState } from 'react';
import { ToggleRow } from '../components/ToggleRow.js';
import { useT } from '../i18n/index.js';
import { useDevicePref } from '../lib/devicePrefs.js';
import { hapticsSupported } from '../lib/haptics.js';
import { isStandalone, promptInstall } from '../lib/installPrompt.js';
import { useInstallable } from '../lib/useInstallable.js';
import { useReducedMotion } from '../lib/useReducedMotion.js';
import { wakeLockSupported } from '../lib/useWakeLock.js';

interface DeviceScreenProps {
  onBack: () => void;
}

/**
 * Settings that belong to the phone rather than to the person.
 *
 * Kept off the account screen on purpose. These answers are about *this*
 * device — whether it may buzz, whether it may hold its screen awake — and
 * they have to be available to somebody who never signs in, which the account
 * screen by definition is not.
 */
export function DeviceScreen({ onBack }: DeviceScreenProps) {
  const t = useT();
  const [haptics, setHaptics] = useDevicePref('haptics');
  const [keepAwake, setKeepAwake] = useDevicePref('keepAwake');
  const reducedMotion = useReducedMotion();
  const canVibrate = hapticsSupported();
  const canKeepAwake = wakeLockSupported();
  const installable = useInstallable();
  const [installed] = useState(isStandalone);

  return (
    <div className="screen">
      <div className="row row--between">
        <h1>{t('device.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <p className="muted small">{t('device.intro')}</p>

      <div className="panel stack">
        <ToggleRow
          label={t('device.haptics')}
          hint={canVibrate ? t('device.hapticsHint') : t('device.hapticsUnsupported')}
          checked={haptics && canVibrate}
          disabled={!canVibrate}
          onChange={setHaptics}
        />
        <ToggleRow
          label={t('device.keepAwake')}
          hint={canKeepAwake ? t('device.keepAwakeHint') : t('device.keepAwakeUnsupported')}
          checked={keepAwake && canKeepAwake}
          disabled={!canKeepAwake}
          onChange={setKeepAwake}
        />
      </div>

      {/* The same offer as on the home screen, kept here for anybody who
          waved that one away and changed their mind. */}
      {installed || installable ? (
        <div className="panel stack stack--tight">
          <h2>{t('install.title')}</h2>
          {installed ? (
            <p className="muted small">{t('device.installed')}</p>
          ) : (
            <>
              <p className="muted small">{t('device.installHint')}</p>
              <button type="button" className="btn btn--block" onClick={() => void promptInstall()}>
                {t('device.install')}
              </button>
            </>
          )}
        </div>
      ) : null}

      {/* Not a switch: this one is the system's answer, and an app-level
          override would only disagree with it. Shown because a setting that
          silently takes effect is indistinguishable from a bug. */}
      <div className="panel stack stack--tight">
        <h2>{t('device.motionTitle')}</h2>
        <p className="muted small">
          {reducedMotion ? t('device.motionReduced') : t('device.motionFull')}
        </p>
      </div>
    </div>
  );
}
